import React from 'react';
import classNames from 'classnames';
import { Route, NavLink, Routes, Navigate } from 'react-router-dom';

import AlertStatic from '@buyside/components/presentation/AlertStatic/alertStatic';
import Button, { BUTTON_SIZES, BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import FollowButton, {
  FollowableType
} from '@buyside/components/presentation/FollowButton/followButton';
import DelayedAccessAlert, {
  Location
} from '@buyside/components/presentation/AccessControl/delayedAccessAlert';
import LoadingSpinner, {
  SIZES
} from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';
import ProfileImage from '@buyside/components/presentation/ProfileImage/profileImage';
import PublicUserFollowers from '@buyside/components/presentation/PublicUserPage/publicUserFollowers';
import PublicUserFollowing from '@buyside/components/presentation/PublicUserPage/publicUserFollowing';
import PublicUserIdeas from '@buyside/components/presentation/PublicUserPage/publicUserIdeas';

import { defaultUserImageUrl, profileUrl } from '@buyside/util/urlHelper';
import { useIsMobile } from '@buyside/util/hooks/useIsMobile';

import { useMainNavigationCurrentUserQuery, usePublicUserQuery } from '@interfaces/graphql-typings';

import styles from './publicUserPageStyles.scss';

const PublicUserPage: React.FC<{
  anonymous: boolean;
  userId: string;
}> = ({ anonymous, userId }) => {
  const isMobile = useIsMobile();

  const { data, error, loading } = usePublicUserQuery({ variables: { anonymous, userId } });
  const { data: userData } = useMainNavigationCurrentUserQuery();

  if (loading) return <LoadingSpinner size={SIZES.LARGE} />;

  if (!data?.userLookUp || error) throw new Error();

  const canViewUserDetails = userData?.currentUser?.canViewUserDetails;
  const {
    activeFollowedUsersCount,
    activeFollowersCount,
    displayName,
    firstName,
    profile,
    profileImageUrl,
    publishedIdeasCount,
    username
  } = data.userLookUp;
  const {
    id: profileId,
    ideaAnnualizedReturnMedian,
    ideaReturnVsBenchmarkAverage,
    ideaTotalReturnAverage
  } = profile || {};

  const isCurrentUser = userId === userData?.currentUser?.id;
  const showUserDetails = (canViewUserDetails || isCurrentUser) && !anonymous;

  let safeDisplayName = 'This Member';
  let safeFirstName = 'This Member';

  if (anonymous && username) {
    safeDisplayName = username;
    safeFirstName = username;
  } else if (showUserDetails) {
    safeDisplayName = displayName;
    safeFirstName = firstName;
  }

  const navItems = [
    {
      name: 'ideas',
      to: '/research/ideas',
      count: publishedIdeasCount
    },
    {
      name: 'following',
      to: '/following',
      count: activeFollowedUsersCount,
      isHidden: anonymous
    },
    {
      name: 'followers',
      to: '/followers',
      count: activeFollowersCount,
      isHidden: anonymous
    }
  ];

  return (
    <div>
      <div className={styles.publicUserPageHeader}>
        <div>
          <ProfileImage source={showUserDetails ? profileImageUrl : defaultUserImageUrl()} />
          <h2>{safeDisplayName}</h2>
        </div>
        {anonymous ? null : (
          <div>
            {isCurrentUser ? null : (
              <FollowButton
                analyticsTag="follow-member"
                buttonDisplay={safeFirstName}
                id={userId}
                size={isMobile ? BUTTON_SIZES.TINY : BUTTON_SIZES.SMALL}
                type={FollowableType.USER}
              />
            )}
            {showUserDetails && profileId ? (
              <Button
                size={isMobile ? BUTTON_SIZES.TINY : BUTTON_SIZES.SMALL}
                text={`View ${safeFirstName}'s Profile`}
                type={BUTTON_TYPES.MAJOR}
                url={profileUrl(profileId)}
              />
            ) : null}
          </div>
        )}
      </div>
      <div className={styles.columns}>
        <div className={styles.datapointsWrapper}>
          <h5>Research Performance (Ideas)</h5>
          <div className={styles.datapoints}>
            {[
              {
                name: 'Total Return (Average)',
                value: ideaTotalReturnAverage
              },
              {
                name: 'Return vs Benchmark (Average)',
                value: ideaReturnVsBenchmarkAverage
              },
              {
                name: 'Annualized Return (Median)',
                value: ideaAnnualizedReturnMedian
              }
            ].map(datapoint => (
              <div key={`${datapoint.name}-performance`}>
                <label>{datapoint.name}</label>
                <p>{datapoint.value}</p>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className={styles.tabNavigation}>
            <div className={styles.tabList}>
              {navItems.map(item =>
                item.isHidden ? null : (
                  <NavLink
                    className={({ isActive }) =>
                      classNames(styles.tabItem, { [styles.active]: isActive })
                    }
                    id={`public-${item.name}-link`}
                    key={`${item.name}-link`}
                    to={item.to}
                  >
                    {item.name}
                    <span className={styles.badge}>{item.count}</span>
                  </NavLink>
                )
              )}
            </div>
          </div>
          <Routes>
            <Route
              path="/research/ideas"
              element={
                publishedIdeasCount > 0 ? (
                  <>
                    <DelayedAccessAlert id={Location.publicUserIdeas} userId={userId} />
                    <PublicUserIdeas
                      anonymous={anonymous}
                      showUserDetails={showUserDetails}
                      userId={userId}
                    />
                  </>
                ) : (
                  <AlertStatic>{safeFirstName} does not have any active research.</AlertStatic>
                )
              }
            />
            <Route
              path="/following"
              element={
                anonymous ? (
                  <Navigate
                    to="/research/ideas"
                    state={{ cannotEditMessage: 'This user is anonymous.' }}
                  />
                ) : (
                  <PublicUserFollowing
                    activeFollowedUsersCount={activeFollowedUsersCount}
                    canViewUserDetails={Boolean(canViewUserDetails)}
                    safeFirstName={safeFirstName}
                    userId={userId}
                  />
                )
              }
            />
            <Route
              path="/followers"
              element={
                anonymous ? (
                  <Navigate
                    to="/research/ideas"
                    state={{ cannotEditMessage: 'This user is anonymous.' }}
                  />
                ) : (
                  <PublicUserFollowers
                    activeFollowersCount={activeFollowersCount}
                    canViewUserDetails={Boolean(canViewUserDetails)}
                    safeFirstName={safeFirstName}
                    userId={userId}
                  />
                )
              }
            />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default PublicUserPage;
